import { motion } from 'framer-motion';
import {
  Mail,
  Shield,
  ShieldOff,
  User,
  Users,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';

interface AdminUser {
  id: string;
  email: string;
  username?: string;
  name?: string;
  is_admin: boolean;
  role?: string;
  auth_methods: string[];
  created_at: string;
  last_sign_in_at?: string;
}

const AdminUsers: React.FC = () => {
  const { getAuthToken } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 50;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAuthToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }
      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
      const response = await fetch(
        `${baseUrl}/api/admin/users?page=${page}&perPage=${perPage}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        if (response.status === 403) {
          setError('Admin access required');
          return;
        }
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setUsers(data.users || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              User Management
            </h1>
            <p className="text-xl text-gray-600">
              View and manage all authenticated users across all auth methods
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/admin/exercises"
              className="px-4 py-2 text-gray-600 hover:text-hexagon hover:bg-white/50 rounded-lg transition-colors"
            >
              Version Manager
            </Link>
            <Link
              to="/admin/sync"
              className="px-4 py-2 text-gray-600 hover:text-hexagon hover:bg-white/50 rounded-lg transition-colors"
            >
              Sync Dashboard
            </Link>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="glass-effect rounded-2xl p-8"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-hexagon" />
            All Users ({total})
          </h2>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-hexagon hover:bg-white/50 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-12 h-12 text-hexagon animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    User
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Auth methods
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Admin
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Created
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Last sign-in
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="py-4 px-4">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          {u.username ? (
                            <span className="font-medium text-gray-900 flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {u.username}
                            </span>
                          ) : null}
                          <span className="text-gray-600 flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {u.email}
                          </span>
                        </div>
                        {u.name && (
                          <span className="text-sm text-gray-500">{u.name}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-wrap gap-2">
                        {u.auth_methods.map((m) => (
                          <span
                            key={m}
                            className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {u.is_admin ? (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <Shield className="w-4 h-4" />
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400">
                          <ShieldOff className="w-4 h-4" />
                          —
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {formatDate(u.last_sign_in_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && users.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            No users found.
          </div>
        )}

        {total > perPage && (
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="py-2 text-gray-600">
              Page {page} of {Math.ceil(total / perPage)}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.ceil(total / perPage)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminUsers;
